/* CartValidator Module
 * Validates cart items against selected delivery date
 * Part of Route Protection Bypass refactoring
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof window.CartValidator === 'undefined') {

class CartValidator {
  constructor() {
    this.LOG = '[CartValidator]';
  }

  /**
   * Validates all cart items against the selected delivery date
   * @param {Array} cartItems - Array of Shopify cart item objects
   * @param {string} deliveryDate - ISO date string (YYYY-MM-DD)
   * @returns {Object} Validation result with available and unavailable items
   */
  validateCart(cartItems, deliveryDate) {
    if (!Array.isArray(cartItems)) {
      console.warn(this.LOG, 'Invalid cartItems provided:', cartItems);
      return { valid: true, available: [], unavailable: [] };
    }

    if (!deliveryDate) {
      console.warn(this.LOG, 'No delivery date provided, treating all items as available');
      return {
        valid: true,
        available: cartItems.map(item => ({
          key: item.key,
          variantId: item.variant_id,
          title: item.title || item.product_title
        })),
        unavailable: []
      };
    }

    const available = [];
    const unavailable = [];

    cartItems.forEach(item => {
      const validation = this.validateItem(item, deliveryDate);
      
      if (validation.available) {
        available.push({
          key: item.key,
          variantId: item.variant_id,
          title: item.title || item.product_title
        });
      } else {
        unavailable.push({
          key: item.key,
          variantId: item.variant_id,
          title: item.title || item.product_title,
          availableFrom: validation.availableFrom,
          availableTo: validation.availableTo
        });
      }
    });

    return {
      valid: unavailable.length === 0,
      available,
      unavailable
    };
  }

  /**
   * Checks if a single item is available for the given date
   * @param {Object} item - Shopify cart item object
   * @param {string} deliveryDate - ISO date string (YYYY-MM-DD)
   * @returns {Object} Item validation result
   */
  validateItem(item, deliveryDate) {
    // Check if this is a Route product - always available
    if (this.isRouteProduct(item)) {
      return {
        available: true,
        availableFrom: null,
        availableTo: null
      };
    }

    // Get metafields from item properties (stored when added to cart)
    let availableFrom = item.properties?.['Available From'] || null;
    let availableTo = item.properties?.['Available To'] || null;

    // If metafields not in properties, try to get from DOM (fallback)
    if (!availableFrom && !availableTo && item.variant_id) {
      const metafields = this.getProductMetafields(item.variant_id.toString(), item.product_id?.toString());
      availableFrom = metafields.availableFrom;
      availableTo = metafields.availableTo;
    }

    // If no metafields, treat as always available (Requirement 2.3)
    if (!availableFrom && !availableTo) {
      return {
        available: true,
        availableFrom: null,
        availableTo: null
      };
    }

    // Validate date range (Requirement 2.2)
    const isAvailable = this._isDateInRange(deliveryDate, availableFrom, availableTo);

    return {
      available: isAvailable,
      availableFrom: availableFrom,
      availableTo: availableTo
    };
  }

  /**
   * Checks if an item is a Route protection product
   * @param {Object} item - Shopify cart item object
   * @returns {boolean} True if Route product
   */
  isRouteProduct(item) {
    if (!item) return false;

    // Check vendor
    if (item.vendor && item.vendor.toLowerCase().includes('route')) {
      return true;
    }

    // Check product type
    if (item.product_type && 
        (item.product_type.toLowerCase().includes('route') || 
         item.product_type.toLowerCase().includes('insurance') ||
         item.product_type.toLowerCase().includes('protection'))) {
      return true;
    }

    // Check SKU
    if (item.sku && item.sku.toLowerCase().includes('route')) {
      return true;
    }

    // Check product title
    if (item.product_title && item.product_title.toLowerCase().includes('route')) {
      return true;
    }

    return false;
  }

  /**
   * Gets product metafields from DOM
   * @param {string} variantId - Variant ID
   * @param {string} productId - Product ID (optional)
   * @returns {Object} Metafields object
   */
  getProductMetafields(variantId, productId = null) {
    try {
      let card = null;

      // Strategy 1: Find by variant ID in data-variants-ids attribute
      if (variantId) {
        // Try exact match first
        card = document.querySelector(`[data-variants-ids="${variantId}"]`);

        // Try substring match for comma-separated lists
        if (!card) {
          card = document.querySelector(`[data-variants-ids*="${variantId}"]`);
        }

        // More robust: check all cards and see if variantId is in the list
        if (!card) {
          const allCards = document.querySelectorAll('[data-variants-ids]');
          for (const candidateCard of allCards) {
            const variantsIds = candidateCard.getAttribute('data-variants-ids');
            if (variantsIds && variantsIds.split(',').includes(variantId.toString())) {
              card = candidateCard;
              break;
            }
          }
        }
      }

      // Strategy 2: Find by product ID as fallback
      if (!card && productId) {
        card = document.querySelector(`[data-product-id="${productId}"]`);
      }

      // Strategy 3: Check if we're on a product page
      if (!card) {
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          card = productForm.closest('[data-product-id]') ||
                 productForm.querySelector('[data-product-id]') ||
                 productForm.closest('.product') ||
                 document.querySelector('.product[data-product-id]') ||
                 document.querySelector('[data-product-id]');
        }
      }

      // Strategy 4: Try to find any element with metafields
      if (!card && variantId) {
        const mainProduct = document.querySelector('main [data-product-id], .product-single [data-product-id]');
        if (mainProduct) {
          card = mainProduct;
        }
      }

      if (!card) {
        return { availableFrom: null, availableTo: null };
      }

      const availableFrom = card.getAttribute('data-available-from')?.trim() || null;
      const availableTo = card.getAttribute('data-available-to')?.trim() || null;

      return {
        availableFrom: availableFrom || null,
        availableTo: availableTo || null
      };
    } catch (e) {
      console.warn(this.LOG, 'Failed to get product metafields:', e);
      return { availableFrom: null, availableTo: null };
    }
  }

  /**
   * Check if a date is within the availableFrom/availableTo range
   * @private
   * @param {string} date - ISO date string (YYYY-MM-DD)
   * @param {string|null} availableFrom - ISO date string or null
   * @param {string|null} availableTo - ISO date string or null
   * @returns {boolean} True if date is in range
   */
  _isDateInRange(date, availableFrom, availableTo) {
    if (!date) return false;

    try {
      const norm = window.DateNormalize ? window.DateNormalize.toISO.bind(window.DateNormalize) : (d) => d ? String(d).slice(0,10) : null;
      const checkDate = new Date((norm(date) || date) + 'T00:00:00');

      if (availableFrom) {
        const fromDate = new Date((norm(availableFrom) || availableFrom) + 'T00:00:00');
        if (checkDate < fromDate) return false;
      }

      if (availableTo) {
        const toDate = new Date((norm(availableTo) || availableTo) + 'T00:00:00');
        if (checkDate > toDate) return false;
      }

      return true;
    } catch (e) {
      console.warn(this.LOG, 'Error parsing dates:', e);
      return false;
    }
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartValidator;
}

// Make available globally for browser usage
if (typeof window !== 'undefined') {
  window.CartValidator = CartValidator;
}

} // End of CartValidator declaration guard
